import React, { useEffect, useState } from 'react';
import { Download, ExternalLink, Upload, X } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { useToast } from '../../components/ui/Toast';
import { ProductImportJob, productsApi } from '../../api/products';

interface Props {
  initialJobId?: string | null;
  onClose: () => void;
  onImported: () => void;
}

type Source = 'csv' | 'shopify' | 'woocommerce';

export const ProductImportModal: React.FC<Props> = ({
  initialJobId,
  onClose,
  onImported,
}) => {
  const toast = useToast();
  const [source, setSource] = useState<Source>('csv');
  const [loading, setLoading] = useState(false);
  const [job, setJob] = useState<ProductImportJob | null>(null);
  const [csvFileName, setCsvFileName] = useState('');
  const [shopDomain, setShopDomain] = useState('');
  const [shopifyToken, setShopifyToken] = useState('');
  const [showManualShopify, setShowManualShopify] = useState(false);
  const [wooUrl, setWooUrl] = useState('');
  const [wooKey, setWooKey] = useState('');
  const [wooSecret, setWooSecret] = useState('');

  useEffect(() => {
    if (!initialJobId) return;
    let cancelled = false;
    setSource('shopify');
    setLoading(true);
    productsApi
      .getImportJob(initialJobId)
      .then((data) => {
        if (!cancelled) setJob(data);
      })
      .catch((err) => {
        if (!cancelled) toast.error(err, 'Could not load Shopify preview.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [initialJobId, toast]);

  async function previewCsv(file: File) {
    const csvText = await file.text();
    setCsvFileName(file.name);
    setLoading(true);
    try {
      setJob(await productsApi.previewCsv({ csvText, fileName: file.name }));
      toast.success('CSV preview is ready.');
    } catch (err) {
      toast.error(err, 'Could not preview CSV.');
    } finally {
      setLoading(false);
    }
  }

  async function previewPlatform() {
    setLoading(true);
    try {
      if (source === 'shopify') {
        setJob(
          await productsApi.previewShopify({
            shopDomain,
            accessToken: shopifyToken,
          }),
        );
      } else {
        setJob(
          await productsApi.previewWooCommerce({
            storeUrl: wooUrl,
            consumerKey: wooKey,
            consumerSecret: wooSecret,
          }),
        );
      }
      toast.success('Import preview is ready.');
    } catch (err) {
      toast.error(err, 'Could not preview store products.');
    } finally {
      setLoading(false);
    }
  }

  async function connectShopify() {
    setLoading(true);
    try {
      const data = await productsApi.createShopifyConnectUrl({ shopDomain });
      window.location.href = data.authUrl;
    } catch (err) {
      toast.error(err, 'Could not start Shopify connection.');
      setLoading(false);
    }
  }

  async function commit() {
    if (!job) return;
    setLoading(true);
    try {
      const committed = await productsApi.commitImport(job.id);
      setJob(committed);
      toast.success(`${committed.committedItems} product(s) imported as drafts.`);
      onImported();
    } catch (err) {
      toast.error(err, 'Could not import products.');
    } finally {
      setLoading(false);
    }
  }

  async function downloadTemplate() {
    try {
      const blob = await productsApi.downloadCsvTemplate();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'payiq-products-template.csv';
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err, 'Could not download template.');
    }
  }

  return (
    <div style={overlayStyle} onClick={onClose}>
      <aside style={panelStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18 }}>Import products</h2>
            <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: 13 }}>
              Bring products from CSV, Shopify, or WooCommerce. Imports are
              saved as drafts for review.
            </p>
          </div>
          <button onClick={onClose} style={iconButtonStyle} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {(['csv', 'shopify', 'woocommerce'] as Source[]).map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => {
                  setSource(item);
                  setJob(null);
                }}
                style={{
                  ...tabStyle,
                  background: source === item ? 'var(--primary)' : 'var(--bg-muted)',
                  color: source === item ? 'white' : 'var(--text-primary)',
                }}
              >
                {item === 'csv' ? 'CSV' : item === 'shopify' ? 'Shopify' : 'WooCommerce'}
              </button>
            ))}
          </div>

          {source === 'csv' ? (
            <div style={sourceCardStyle}>
              <Button variant="secondary" type="button" onClick={downloadTemplate}>
                <Download size={15} /> Download CSV template
              </Button>
              <label style={uploadBoxStyle}>
                <Upload size={20} />
                <strong>{csvFileName || 'Choose CSV file'}</strong>
                <span>Products with the same handle or parent SKU become variants.</span>
                <input
                  type="file"
                  accept=".csv,text/csv"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void previewCsv(file);
                  }}
                />
              </label>
            </div>
          ) : source === 'shopify' ? (
            <div style={sourceCardStyle}>
              <Input label="Shop domain" value={shopDomain} placeholder="store.myshopify.com" onChange={(e) => setShopDomain(e.target.value)} />
              <div style={helperBoxStyle}>
                <strong>Connect through Shopify</strong>
                <span>
                  You will approve PayIQ on Shopify, then return here to review
                  imported products as drafts. PayIQ does not show or store your
                  Shopify access token in this browser.
                </span>
              </div>
              <Button onClick={connectShopify} loading={loading} disabled={!shopDomain}>
                <ExternalLink size={15} /> Connect Shopify store
              </Button>
              <button
                type="button"
                style={textButtonStyle}
                onClick={() => setShowManualShopify((value) => !value)}
              >
                {showManualShopify
                  ? 'Hide manual token import'
                  : 'Use manual token instead'}
              </button>
              {showManualShopify ? (
                <div style={manualBoxStyle}>
                  <Input label="Admin API access token" value={shopifyToken} type="password" onChange={(e) => setShopifyToken(e.target.value)} />
                  <Button variant="secondary" onClick={previewPlatform} loading={loading} disabled={!shopDomain || !shopifyToken}>
                    Preview Shopify products manually
                  </Button>
                </div>
              ) : null}
            </div>
          ) : (
            <div style={sourceCardStyle}>
              <Input label="Store URL" value={wooUrl} placeholder="https://shop.example.com" onChange={(e) => setWooUrl(e.target.value)} />
              <Input label="Consumer key" value={wooKey} onChange={(e) => setWooKey(e.target.value)} />
              <Input label="Consumer secret" value={wooSecret} type="password" onChange={(e) => setWooSecret(e.target.value)} />
              <Button onClick={previewPlatform} loading={loading} disabled={!wooUrl || !wooKey || !wooSecret}>
                Preview WooCommerce products
              </Button>
            </div>
          )}

          {job ? (
            <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
              <div style={{ padding: 12, background: 'var(--bg-muted)', fontSize: 13 }}>
                {job.totalItems} item(s) · {job.readyItems} ready · {job.warningItems} with warnings · {job.errorItems} blocked · {job.committedItems} committed
              </div>
              <div style={{ maxHeight: 300, overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th style={th}>Product</th>
                      <th style={th}>Variants</th>
                      <th style={th}>Status</th>
                      <th style={th}>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {job.items.map((item) => (
                      <tr key={item.id} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={td}>{item.normalized.name}</td>
                        <td style={td}>{item.normalized.variants?.length ?? 0}</td>
                        <td style={td}>{item.status}</td>
                        <td style={td}>
                          {[...item.errors, ...item.warnings].join(' ') || 'Ready'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>

        <div style={footerStyle}>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Close
          </Button>
          <Button onClick={commit} loading={loading} disabled={!job || job.status === 'committed'}>
            Import as drafts
          </Button>
        </div>
      </aside>
    </div>
  );
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 120,
  background: 'rgba(11, 13, 15, 0.42)',
  display: 'flex',
  justifyContent: 'flex-end',
};

const panelStyle: React.CSSProperties = {
  width: 720,
  maxWidth: '100%',
  height: '100%',
  background: 'var(--bg-card)',
  boxShadow: 'var(--shadow-lg)',
  display: 'flex',
  flexDirection: 'column',
};

const headerStyle: React.CSSProperties = {
  padding: '18px 20px',
  borderBottom: '1px solid var(--border)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
};

const iconButtonStyle: React.CSSProperties = {
  border: 0,
  background: 'var(--bg-muted)',
  borderRadius: 'var(--radius-full)',
  width: 34,
  height: 34,
  display: 'grid',
  placeItems: 'center',
  cursor: 'pointer',
};

const tabStyle: React.CSSProperties = {
  border: 0,
  borderRadius: 'var(--radius-full)',
  padding: '8px 12px',
  fontWeight: 700,
  cursor: 'pointer',
};

const sourceCardStyle: React.CSSProperties = {
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md)',
  padding: 14,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

const helperBoxStyle: React.CSSProperties = {
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md)',
  padding: 12,
  background: 'var(--bg-muted)',
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 13,
  color: 'var(--text-secondary)',
};

const textButtonStyle: React.CSSProperties = {
  border: 0,
  background: 'transparent',
  color: 'var(--primary)',
  fontWeight: 700,
  cursor: 'pointer',
  padding: 0,
  textAlign: 'left',
};

const manualBoxStyle: React.CSSProperties = {
  borderTop: '1px solid var(--border)',
  paddingTop: 12,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

const uploadBoxStyle: React.CSSProperties = {
  border: '1px dashed var(--border)',
  borderRadius: 'var(--radius-md)',
  padding: 18,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 6,
  cursor: 'pointer',
  color: 'var(--text-secondary)',
};

const footerStyle: React.CSSProperties = {
  marginTop: 'auto',
  padding: '16px 20px',
  borderTop: '1px solid var(--border)',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
};

const th: React.CSSProperties = {
  padding: '10px 12px',
  textAlign: 'left',
  color: 'var(--text-muted)',
  fontWeight: 700,
};

const td: React.CSSProperties = {
  padding: '10px 12px',
  verticalAlign: 'top',
};
